import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ABOUT as DEFAULT_ABOUT, SKILLS as DEFAULT_SKILLS, STATS as DEFAULT_STATS, CAREER as DEFAULT_CAREER } from "../data/content";
import { useContent } from "../data/ContentContext";

// Animated counter that runs once when in view
const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
};

const About = () => {
  const { content } = useContent();
  const ABOUT = content?.about || DEFAULT_ABOUT;
  const SKILLS = content?.skills || DEFAULT_SKILLS;
  const STATS = content?.stats || DEFAULT_STATS;
  const CAREER = content?.career || DEFAULT_CAREER;

  return (
    <section id="about" className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14" data-testid="about-section">
      <div className="max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-12 gap-6 mb-12 md:mb-16"
        >
          <div className="col-span-12 md:col-span-1 overline pt-3">— {ABOUT.overline}</div>
          <div className="col-span-12 md:col-span-11">
            <h2 className="editorial-h1 text-4xl md:text-6xl lg:text-7xl">
              {ABOUT.statement}
            </h2>
          </div>
        </motion.div>

        {/* Stats counter row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 gap-4 md:gap-2 mb-20 max-w-3xl"
          data-testid="about-stats"
        >
          {STATS.map((s, i) => (
            <div key={i} className="border-t border-black pt-4 pr-3" data-testid={`stat-${i}`}>
              <div className="font-display text-4xl md:text-6xl tracking-tight">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="text-xs md:text-sm uppercase tracking-[0.15em] mt-2 text-black/65 font-medium">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Bio + portrait */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="col-span-12 md:col-span-7 order-2 md:order-1"
          >
            {ABOUT.body.map((p, i) => (
              <p key={i} className="text-lg md:text-xl leading-relaxed mb-6 text-black/80">
                {p}
              </p>
            ))}

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2" data-testid="about-clients">
              <div className="col-span-2 sm:col-span-3 overline mb-1">— Clients & Partnerships</div>
              {ABOUT.clients.map((c) => (
                <div key={c} className="text-sm md:text-base border-b border-black/15 py-2 font-medium">
                  {c}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 md:col-span-5 md:col-start-8 order-1 md:order-2"
          >
            <div className="relative group">
              <div className="relative overflow-hidden">
                <img
                  src="/media/img/portrait-52.jpg"
                  alt="Mia O'Neill"
                  className="w-full h-auto aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105"
                  data-testid="about-portrait"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[var(--bg)] px-4 py-2 border border-black/20 z-10">
                <span className="overline">On the road</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          id="skills"
          data-testid="about-skills"
          className="mb-24"
        >
          <div className="overline mb-6">— Expertise</div>
          <div className="flex flex-wrap gap-3">
            {SKILLS.map((s, i) => (
              <motion.span
                key={s.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.04, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="skill-tag"
                style={{ "--tag-color": s.color }}
                data-testid={`skill-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <span className="skill-dot" aria-hidden="true" />
                <span>{s.label}</span>
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Career timeline */}
        <div id="career" className="scroll-mt-24" data-testid="career-section">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-12 gap-6 mb-12"
          >
            <div className="col-span-12 md:col-span-7">
              <div className="overline mb-4">— Career</div>
              <h3 className="editorial-h2 text-4xl md:text-6xl">A track record.</h3>
            </div>
          </motion.div>

          <div className="relative">
            {CAREER.map((c, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.65, delay: i * 0.05 }}
                className="grid grid-cols-12 gap-3 md:gap-6 border-t border-black py-7 md:py-9 group hover:bg-black/[0.03] transition-colors"
                data-testid={`career-entry-${i}`}
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="text-sm uppercase tracking-[0.18em] font-semibold">{c.period}</div>
                  <div className="text-xs text-black/55 mt-1">{c.duration}</div>
                  <div className="text-xs text-black/55 mt-1">{c.location}</div>
                </div>
                <div className="col-span-12 md:col-span-5">
                  <div className="font-display text-2xl md:text-3xl leading-tight">{c.company}</div>
                  <div className="text-sm md:text-base mt-1 text-black/65 font-medium">{c.role}</div>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <p className="text-sm md:text-base text-black/70 leading-relaxed">{c.body}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
